import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { LockOutlined } from "@mui/icons-material";
import { PAGE_STATUS } from "./constants";
import { formatSharedDate } from "./utils";
import { useSharedConversation } from "./useSharedConversation";
import SharedChatHeader from "./components/SharedChatHeader";
import SharedChatWindowHeader from "./components/SharedChatWindowHeader";
import SharedChatStateCard from "./components/SharedChatStateCard";
import SharedChatThread from "./components/SharedChatThread";
import "./SharedChat.scss";

/**
 * Public read-only shared chat page — /share/:token
 * Data flow: URL → API → React state → UI (no client storage).
 */
const SharedChat = () => {
  const { token } = useParams();
  const intl = useIntl();
  const dir = useSelector(({ theme }) => theme.direction) || "rtl";
  const { status, conversation, messagePairs, retry } =
    useSharedConversation(token);

  const title =
    conversation?.title ||
    intl.formatMessage({
      id: "chatbot.sharedDefaultTitle",
      defaultMessage: dir === "rtl" ? "محادثة مشتركة" : "Shared conversation",
    });
  const createdLabel = formatSharedDate(conversation?.createdAt, dir);
  const isSuccess = status === PAGE_STATUS.SUCCESS;

  return (
    <div className="shared-chat-page" dir={dir}>
      <div className="shared-chat-bg" aria-hidden>
        <span className="shared-chat-orb shared-chat-orb--a" />
        <span className="shared-chat-orb shared-chat-orb--b" />
        <span className="shared-chat-orb shared-chat-orb--c" />
        <span className="shared-chat-grid" />
      </div>

      <div className="shared-chat-shell">
        <SharedChatHeader
          dir={dir}
          status={status}
          exchangeCount={messagePairs.length}
        />

        <section className="shared-chat-window">
          <SharedChatWindowHeader
            dir={dir}
            status={status}
            title={title}
            createdLabel={createdLabel}
          />

          <main className="shared-chat-main">
            {!isSuccess && (
              <SharedChatStateCard
                status={status}
                dir={dir}
                onRetry={retry}
              />
            )}
            {isSuccess && (
              <SharedChatThread messagePairs={messagePairs} dir={dir} />
            )}
          </main>

          <footer className="shared-chat-window-footer">
            <LockOutlined fontSize="inherit" />
            <span>
              {intl.formatMessage({
                id: "chatbot.sharedFooterNote",
                defaultMessage:
                  dir === "rtl"
                    ? "هذه نسخة للقراءة فقط — لا يمكن إرسال رسائل أو تعديل المحادثة."
                    : "This is a read-only snapshot — messaging and edits are disabled.",
              })}
            </span>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default SharedChat;
